import React, { useState, useRef, useEffect, useMemo } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import {
  addCropDiseaseMapping,
  updateCropDiseaseMapping,
  // ✅ CHANGED: getCrops -> getCropShort
  getCropShort,
  getAllDiseases,
  getAllPhases,
  getLanguages,
  translateData,
} from '../lib/api';
import { Icon } from '@iconify/react/dist/iconify.js';

/* ---------- Styles (unchanged) ---------- */
const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1.5rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .form-grid-container > div {
    width: 100 !important;
    box-sizing: border-box !important;
  }
  .form-control, textarea {
    width: 100% !important;
    box-sizing: border-box !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
    font-size: 0.875rem !important;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
  }
  .form-control:focus, textarea:focus {
    border-color: #1a73e8 !important;
    box-shadow: 0 0 0 0.2rem rgba(26,115,232,0.25) !important;
    outline: none !important;
  }
  textarea {
    height: 80px !important;
    resize: vertical !important;
  }
  .error-message {
    color: #dc3545;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
  .success-message {
    color: #28a745;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    text-align: center;
  }
  .card-body {
    overflow-y: auto !important;
    max-height: calc(100vh - 80px) !important;
    padding: 1.5rem !important;
    overscroll-behavior: contain !important;
  }
  .add-button { margin-top: 0.5rem; }
  .phase-variants-table { margin-top: 1rem; }
  .tables-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem;
  }
  .table {
    border-collapse: collapse; width: 100%; border: 1px solid #ddd; background: #fff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1); border-radius: 8px; overflow: hidden;
  }
  .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  .table th { background-color: #126B3A; color: #fff; font-weight: bold; }
  .table tr:nth-child(even) { background-color: #f9f9f9; }
  .form-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd' /%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 1em;
    border: 1px solid #ced4da !important; border-radius: 8px !important;
    padding: 8px 28px 8px 12px !important; font-size: 0.875rem !important;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
  }
  .form-select:focus {
    border-color: #1a73e8 !important;
    box-shadow: 0 0 0 0.2rem rgba(26,115,232,0.25) !important;
    outline: none !important;
  }
  .form-select:disabled {
    background-color: #f8fafc; color: #6b7280; cursor: not-allowed; opacity: 0.65;
  }
  .bullet-list { list-style-type: disc; padding-left: 20px; }
  .bullet-list li { margin-bottom: 4px; }
  .remove-btn {
    background-color: #dc3545; color: #fff; border: none; padding: 2px 6px;
    border-radius: 4px; cursor: pointer; font-size: 0.75rem;
  }
  .remove-btn:hover { background-color: #c82333; }

  /* Tabs */
  .main-tabs { display: flex; gap: 8px; margin-bottom: 12px; }
  .main-tab {
    padding: 8px 14px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
  }
  .main-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }
  .lang-tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
  .lang-tab {
    padding: 6px 10px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
  }
  .lang-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }

  /* Small Chip style for translated names line */
  .chip {
    display:inline-block;
    padding:4px 8px;
    border:1px solid #e5e7eb;
    background:#f9fafb;
    border-radius:6px;
    font-size:12px;
    margin-right:6px;
    margin-bottom:6px;
  }

`;

/* ---------- Helpers ---------- */
const parseLangContainerToMap = (value) => {
  if (!value) return {};

  if (Array.isArray(value)) {
    if (value.length === 0) return {};
    try {
      const str = value[0];
      const parsed = JSON.parse(str);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  if (typeof value === "object") return value;

  return {};
};

const safeParse = (val) => {
  if (!val) return {};
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return {}; }
};

const getLangMap = (container, key) => {
  const parsed = safeParse(container);
  if (!parsed) return {};
  const obj = parsed[key] || parsed[key?.charAt(0).toUpperCase() + key?.slice(1)] || parsed;
  return typeof obj === 'object' ? obj : {};
};

// Updated to handle multiple diseases
const normalizeEditPayload = (src) => {
  if (!src?.Diseases) return null;

  return {
    cropDiseasesId: src.CropDiseasesID,
    cropId: src.CropID,
    cropName: src.CropName,
    diseases: src.Diseases.map(d => ({
      diseaseId: d.DiseasesID,
      diseaseName: d.DiseaseName,
      phases: d.Phases.map(p => ({
        phase: p.PhaseName,
        symptoms: Array.isArray(p.Symptoms) ? p.Symptoms : [p.Symptoms],
        cure: Array.isArray(p.Cure) ? p.Cure : [p.Cure],
        SymptomsOtherLang: parseLangContainerToMap(p.SymptomsOtherLang),
        CureOtherLang: parseLangContainerToMap(p.CureOtherLang),
        tr: {}
      }))
    }))
  };
};

const AddDiseaseMapping = ({
  isOpen,
  onClose,
  onSubmit,
  diseaseMappingToEdit,
  onSuccess,
}) => {
  const cropInputRef = useRef(null);
  const [message, setMessage] = useState(null);

  const [mainTab, setMainTab] = useState('english');
  const [step, setStep] = useState('basic');

  const [selectedLang, setSelectedLang] = useState('');
  const [languages, setLanguages] = useState([]);

  const [selectedCrop, setSelectedCrop] = useState('');
  const [selectedCropId, setSelectedCropId] = useState('');
  const [selectedDisease, setSelectedDisease] = useState('');
  const [selectedDiseaseId, setSelectedDiseaseId] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('');

  const [symptoms, setSymptoms] = useState('');
  const [cure, setCure] = useState('');
  const [symptomsList, setSymptomsList] = useState([]);
  const [curesList, setCuresList] = useState([]);

  // Combine all diseases and their phases into one array
  const [allPhaseVariants, setAllPhaseVariants] = useState([]);

  const [cropDisabled, setCropDisabled] = useState(false);
  const [diseaseDisabled, setDiseaseDisabled] = useState(false);

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const [crops, setCrops] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [phases, setPhases] = useState([]);

  // Other-lang per-row editor state
  const [editingOtherLangRowIndex, setEditingOtherLangRowIndex] = useState(null);
  const [otherLangSymptomsInput, setOtherLangSymptomsInput] = useState('');
  const [otherLangCureInput, setOtherLangCureInput] = useState('');

  // Separate lists for other language symptoms and cures (for table display)
  const [otherLangSymptomsList, setOtherLangSymptomsList] = useState([]);
  const [otherLangCuresList, setOtherLangCuresList] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    clearErrors,
  } = useForm({
    defaultValues: {
      crop: '',
      disease: '',
      phase: '',
      symptoms: '',
      cure: '',
    },
  });

  const selectedLangName = useMemo(
    () => languages.find(l => l.GoogleLanguageCode === selectedLang)?.LanguageName || selectedLang || '',
    [languages, selectedLang]
  );

  // Get normalized data for editing
  const normalizedEditData = useMemo(() =>
    normalizeEditPayload(diseaseMappingToEdit),
    [diseaseMappingToEdit]
  );

  // Get all diseases from normalized data
  const allDiseases = useMemo(() => {
    if (!normalizedEditData || !normalizedEditData.diseases) return [];
    return normalizedEditData.diseases;
  }, [normalizedEditData]);

  // Get disease names for display (not used in UI now but kept)
  const diseaseNames = useMemo(() => {
    return allDiseases.map(d => d.diseaseName).join(', ');
  }, [allDiseases]);

  /* ---------- Load lists when open ---------- */
  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      setFetchLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const [cropsData, diseasesData, phasesData, langsData] = await Promise.all([
          // ✅ CHANGED: getCrops -> getCropShort
          getCropShort(token),
          getAllDiseases(token),
          getAllPhases(token),
          getLanguages(),
        ]);
        setCrops(cropsData?.data || cropsData || []);
        setDiseases(diseasesData?.data || diseasesData || []);
        setPhases(phasesData?.data.phases || phasesData || []);
        const langList = langsData?.data || langsData || [];
        setLanguages(langList);
        if (langList.length > 0) setSelectedLang(langList[0].GoogleLanguageCode);
      } catch (error) {
        console.error('Error fetching data:', error);
        setMessage({ type: 'error', text: 'Failed to load data.' });
      } finally {
        setFetchLoading(false);
      }
    };
    fetchData();
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  /* ---------- Focus crop input when open ---------- */
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      cropInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(t);
  }, [isOpen]);

  /* ---------- Apply edit data after lists are present ---------- */
  useEffect(() => {
    if (!isOpen) return;
    if (crops.length === 0 && diseases.length === 0) return;

    if (normalizedEditData) {
      // Set crop data
      setValue('crop', normalizedEditData.cropName || '');
      setSelectedCrop(normalizedEditData.cropName || '');
      setSelectedCropId(normalizedEditData.cropId || '');

      // Combine all diseases' phases into one array
      const allPhases = normalizedEditData.diseases.flatMap(disease =>
        disease.phases.map(phase => ({
          ...phase,
          diseaseId: disease.diseaseId,
          diseaseName: disease.diseaseName
        }))
      );
      setAllPhaseVariants(allPhases);

      setCropDisabled(true);
      setDiseaseDisabled(false); // Enable disease selection for adding new
      clearErrors(['crop', 'disease']);
    } else {
      // Creating new
      reset();
      setSelectedCrop('');
      setSelectedCropId('');
      setSelectedDisease('');
      setSelectedDiseaseId('');
      setSelectedPhase('');
      setSymptoms('');
      setCure('');
      setSymptomsList([]);
      setCuresList([]);
      setAllPhaseVariants([]);
      setCropDisabled(false);
      setDiseaseDisabled(false);
    }

    // If editing, jump to Other tab
    if (diseaseMappingToEdit) {
      setStep('translations');
      setMainTab('other');
    } else {
      setStep('basic');
      setMainTab('english');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, crops, diseases, normalizedEditData]);

  /* ---------- Ensure pv.tr is fully populated for all languages ---------- */
  useEffect(() => {
    if (!languages.length || !allPhaseVariants.length) return;

    setAllPhaseVariants(prev => {
      const copy = prev.map(pv => {
        const next = { ...pv };
        next.SymptomsOtherLang = next.SymptomsOtherLang || {};
        next.CureOtherLang = next.CureOtherLang || {};
        next.tr = next.tr || {};

        const enSymptoms = Array.isArray(next.symptoms) ? next.symptoms.join('; ') : next.symptoms || '';
        const enCure = Array.isArray(next.cure) ? next.cure.join('; ') : next.cure || '';

        languages.forEach(({ GoogleLanguageCode }) => {
          const sym = (next.SymptomsOtherLang && next.SymptomsOtherLang[GoogleLanguageCode]) ?? '';
          const cur = (next.CureOtherLang && next.CureOtherLang[GoogleLanguageCode]) ?? '';

          const finalSym = String(sym || next.SymptomsOtherLang?.en || enSymptoms || '').trim();
          const finalCur = String(cur || next.CureOtherLang?.en || enCure || '').trim();

          next.tr[GoogleLanguageCode] = {
            symptoms: finalSym,
            cure: finalCur,
          };
        });

        return next;
      });
      return copy;
    });
  }, [languages, allPhaseVariants.length]);

  const handleCropChange = (e) => {
    const option = e.target.options[e.target.selectedIndex];
    const cropId = option.getAttribute('data-id');
    const cropName = option.text;

    setSelectedCrop(cropName);
    setSelectedCropId(cropId);
    setSelectedDisease('');
    setSelectedDiseaseId('');
    setValue('disease', '');
    setValue('crop', cropName);

    setAllPhaseVariants([]);
    setCropDisabled(false);
  };

  const handleDiseaseChange = (e) => {
    const option = e.target.options[e.target.selectedIndex];
    const diseaseId = option.getAttribute('data-id');
    const diseaseName = option.text;

    setSelectedDisease(diseaseName);
    setSelectedDiseaseId(diseaseId);
    setValue('disease', diseaseName);


    if (e.target.value) setDiseaseDisabled(false);
  };

  const handleAddSymptom = () => {
    if (symptoms.trim() && !symptoms.includes('$')) {
      setSymptomsList((prev) => [...prev, symptoms.trim()]);
      setSymptoms('');
    } else {
      alert('Symptoms cannot contain "$" and must be non-empty.');
    }
  };

  const handleAddCure = () => {
    if (cure.trim() && !cure.includes('$')) {
      setCuresList((prev) => [...prev, cure.trim()]);
      setCure('');
    } else {
      alert('Cure cannot contain "$" and must be non-empty.');
    }
  };

  const handleAddPhaseVariant = () => {
    if (selectedPhase && selectedDiseaseId && symptomsList.length > 0 && curesList.length > 0) {
      // Check if this phase already exists for this disease
      if (allPhaseVariants.some((pv) =>
        pv.phase.toLowerCase() === selectedPhase.toLowerCase() &&
        pv.diseaseId === selectedDiseaseId
      )) {
        alert('This phase has already been added for this disease.');
        return;
      }

      const newPhaseVariant = {
        phase: selectedPhase,
        symptoms: [...symptomsList],
        cure: [...curesList],
        diseaseId: selectedDiseaseId,
        diseaseName: selectedDisease,
        tr: {},
        SymptomsOtherLang: {},
        CureOtherLang: {},
      };

      setAllPhaseVariants((prev) => [...prev, newPhaseVariant]);
      setSelectedPhase('');
      setSymptomsList([]);
      setCuresList([]);
      setSymptoms('');
      setCure('');
      if (allPhaseVariants.length === 0 && !diseaseMappingToEdit) setCropDisabled(true);
    } else {
      alert('Please select disease, phase and add symptoms and cures.');
    }
  };

  const handleRemoveSymptom = (index) =>
    setSymptomsList((prev) => prev.filter((_, i) => i !== index));
  const handleRemoveCure = (index) =>
    setCuresList((prev) => prev.filter((_, i) => i !== index));

  const handleRemovePhaseVariant = (index) => {
    setAllPhaseVariants((prev) => prev.filter((_, i) => i !== index));
    if (allPhaseVariants.length === 1 && !diseaseMappingToEdit) setCropDisabled(false);
  };

  const handleEditPhaseVariant = (index) => {
    const pv = allPhaseVariants[index];
    if (!pv) return;
    setSelectedPhase(pv.phase || '');
    setSelectedDisease(pv.diseaseName || '');
    setSelectedDiseaseId(pv.diseaseId || '');
    setValue('disease', pv.diseaseName || '');
    setSymptomsList(Array.isArray(pv.symptoms) ? pv.symptoms : (pv.symptoms ? [pv.symptoms] : []));
    setCuresList(Array.isArray(pv.cure) ? pv.cure : (pv.cure ? [pv.cure] : []));
    setAllPhaseVariants(prev => prev.filter((_, i) => i !== index));
    setMainTab('english');
  };

  /* ---------- Other Language Handlers ---------- */
  const handleAddOtherLangSymptom = () => {
    if (otherLangSymptomsInput.trim() && !otherLangSymptomsInput.includes('$')) {
      setOtherLangSymptomsList((prev) => [...prev, otherLangSymptomsInput.trim()]);
      setOtherLangSymptomsInput('');
    } else {
      alert('Symptoms cannot contain "$" and must be non-empty.');
    }
  };

  const handleAddOtherLangCure = () => {
    if (otherLangCureInput.trim() && !otherLangCureInput.includes('$')) {
      setOtherLangCuresList((prev) => [...prev, otherLangCureInput.trim()]);
      setOtherLangCureInput('');
    } else {
      alert('Cure cannot contain "$" and must be non-empty.');
    }
  };

  const handleRemoveOtherLangSymptom = (index) =>
    setOtherLangSymptomsList((prev) => prev.filter((_, i) => i !== index));

  const handleRemoveOtherLangCure = (index) =>
    setOtherLangCuresList((prev) => prev.filter((_, i) => i !== index));

  /* ---------- Translate ---------- */
  const translateAllPhaseRows = async () => {
    const newPhaseVariants = [...allPhaseVariants];
    for (let i = 0; i < newPhaseVariants.length; i++) {
      const pv = newPhaseVariants[i];
      const SymptomsStr = (Array.isArray(pv.symptoms) ? pv.symptoms.join('$') : pv.symptoms || '').trim();
      const CureStr = (Array.isArray(pv.cure) ? pv.cure.join('$') : pv.cure || '').trim();

      try {
        const res = await translateData({ Symptoms: SymptomsStr, Cure: CureStr });
        const maybe = res?.data ?? res ?? {};
        const data = maybe?.data && !maybe.Symptoms ? maybe.data : maybe;

        const trSymptoms = data?.Symptoms || {};
        const trCure = data?.Cure || {};

        if (!pv.tr) pv.tr = {};
        if (!pv.SymptomsOtherLang) pv.SymptomsOtherLang = {};
        if (!pv.CureOtherLang) pv.CureOtherLang = {};

        languages.forEach(({ GoogleLanguageCode }) => {
          const sym = trSymptoms?.[GoogleLanguageCode] || SymptomsStr;
          const cur = trCure?.[GoogleLanguageCode] || CureStr;
          pv.tr[GoogleLanguageCode] = { symptoms: sym, cure: cur };
          pv.SymptomsOtherLang[GoogleLanguageCode] = sym;
          pv.CureOtherLang[GoogleLanguageCode] = cur;
        });
      } catch (e) {
        console.error('Translate row failed', e);
        if (!pv.tr) pv.tr = {};
        languages.forEach(({ GoogleLanguageCode }) => {
          const sym = (Array.isArray(pv.symptoms) ? pv.symptoms.join('; ') : pv.symptoms || '');
          const cur = (Array.isArray(pv.cure) ? pv.cure.join('; ') : pv.cure || '');
          pv.tr[GoogleLanguageCode] = { symptoms: sym, cure: cur };
          pv.SymptomsOtherLang = pv.SymptomsOtherLang || {};
          pv.CureOtherLang = pv.CureOtherLang || {};
          pv.SymptomsOtherLang[GoogleLanguageCode] = sym;
          pv.CureOtherLang[GoogleLanguageCode] = cur;
        });
      }
    }
    setAllPhaseVariants(newPhaseVariants);
  };

  const onTranslateClick = async () => {
    if (!selectedCropId || allPhaseVariants.length === 0) {
      setMessage({ type: 'error', text: 'Please select Crop and add at least one Phase Details.' });
      return;
    }
    setStep('translate');
    await translateAllPhaseRows();
    setStep('translations');
    setMainTab('other');
  };

  /* ---------- Name helpers ---------- */
  const currentCropObj = useMemo(
    () => crops.find(c => String(c.ID) === String(selectedCropId)) || crops.find(c => c.CropName === selectedCrop) || null,
    [crops, selectedCropId, selectedCrop]
  );

  // Build a quick lookup for diseases by ID (to fetch OtherLang names)
  const diseaseById = useMemo(() => {
    const map = {};
    for (const d of diseases) {
      map[String(d.ID)] = d;
    }
    return map;
  }, [diseases]);

  const getOtherLangName = (entityObj, entity) => {
    if (!entityObj) return '';
    const key = `${entity}OtherLang`;
    const container = entityObj[key];
    const map = getLangMap(container, entity);
    return map[selectedLang] || '';
  };

  // Get disease name in selected language by DiseaseID
  const getDiseaseOtherLangById = (diseaseId, fallbackName) => {
    const d = diseaseById[String(diseaseId)];
    if (!d) return fallbackName || '';
    const map = getLangMap(d.DiseasesNameOtherLang, 'DiseasesName');
    return map[selectedLang] || d.DiseasesName || fallbackName || '';
  };

  const getOtherLangPhaseName = (phaseEN) => {
    const phaseObj = phases.find(p => p.PhaseName === phaseEN);
    if (!phaseObj) return phaseEN;
    const map = getLangMap(phaseObj.PhaseNameOtherLang, 'PhaseName');
    return map[selectedLang] || phaseEN;
  };

  /* ---------- Save ---------- */
  const onFormSubmit = async () => {
    try {
      setLoading(true);
      setMessage(null);

      if (allPhaseVariants.length === 0) {
        setMessage({ type: 'error', text: 'Please add at least one phase variant.' });
        return;
      }

      const toPayload = (pv) => {
        const enSymptoms = Array.isArray(pv.symptoms) ? pv.symptoms.join('$') : pv.symptoms || '';
        const enCure = Array.isArray(pv.cure) ? pv.cure.join('$') : pv.cure || '';

        const symptomsOther = {};
        const cureOther = {};
        languages.forEach(({ GoogleLanguageCode }) => {
          const t = pv.tr?.[GoogleLanguageCode] || {};
          const s = t.symptoms ?? pv.SymptomsOtherLang?.[GoogleLanguageCode] ?? enSymptoms;
          const c = t.cure ?? pv.CureOtherLang?.[GoogleLanguageCode] ?? enCure;
          symptomsOther[GoogleLanguageCode] = s;
          cureOther[GoogleLanguageCode] = c;
        });

        return {
          DiseasesID: pv.diseaseId,
          PhaseName: pv.phase,
          Symptoms: enSymptoms,
          Cure: enCure,
          SymptomsOtherLang: JSON.stringify(symptomsOther),
          CureOtherLang: JSON.stringify(cureOther),
        };
      };

      const normalized = normalizeEditPayload(diseaseMappingToEdit);
      const token = localStorage.getItem('authToken');

      let response;
      if (normalized?.cropDiseasesId) {
        // For editing - send all phase variants grouped by disease
        const diseasesDetails = allPhaseVariants.map(toPayload);

        const apiData = {
          CropDiseasesID: normalized.cropDiseasesId,
          CropID: selectedCropId,
          DiseasesDetails: diseasesDetails,
        };
        response = await updateCropDiseaseMapping(token, apiData);
      } else {
        // For new mapping
        const apiData = {
          CropID: selectedCropId,
          DiseasesDetails: allPhaseVariants.map(toPayload),
        };
        response = await addCropDiseaseMapping(token, apiData);
      }

      if (response?.message) {
        setMessage({ type: 'success', text: response.message });
        await onSubmit?.();
        onSuccess?.();
        setTimeout(() => {
          setMessage(null);
          reset();
          onClose();
        }, 1200);
      }
    } catch (error) {
      console.error('Error saving disease mapping:', error);
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Failed to save disease mapping.',
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Validation ---------- */
  const isAnyOtherLangEmpty = useMemo(() => {
    if (!languages.length || !allPhaseVariants.length) return true;
    for (const pv of allPhaseVariants) {
      for (const { GoogleLanguageCode } of languages) {
        const t = pv.tr?.[GoogleLanguageCode];
        const s = (t?.symptoms ?? pv.SymptomsOtherLang?.[GoogleLanguageCode] ?? '').toString().trim();
        const c = (t?.cure ?? pv.CureOtherLang?.[GoogleLanguageCode] ?? '').toString().trim();
        if (!s || !c) return true;
      }
    }
    return false;
  }, [languages, allPhaseVariants]);


  const filteredDiseases = useMemo(() => {
    if (!selectedCropId || diseases.length === 0) return [];

    return diseases.filter(d => {
      try {
        const arr = JSON.parse(d.CropIds || "[]");
        return Array.isArray(arr) && arr.includes(selectedCropId);
      } catch {
        return false;
      }
    });
  }, [diseases, selectedCropId]);



  /* ---------- Keep editor inputs synced ---------- */
  useEffect(() => {
    if (editingOtherLangRowIndex == null) return;
    const pv = allPhaseVariants[editingOtherLangRowIndex];
    const t = pv?.tr?.[selectedLang] || {};

    // Split the translated symptoms and cures by semicolon for table display
    const symptomsArray = t.symptoms ? String(t.symptoms).split('$').filter(Boolean).map(s => s.trim()) : [];
    const curesArray = t.cure ? String(t.cure).split('$').filter(Boolean).map(c => c.trim()) : [];

    setOtherLangSymptomsList(symptomsArray);
    setOtherLangCuresList(curesArray);

    // Set the textarea inputs to the joined string
    setOtherLangSymptomsInput(t.symptoms || '');
    setOtherLangCureInput(t.cure || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLang, editingOtherLangRowIndex]);

  const handleOtherLangEditRow = (rowIdx) => {
    setEditingOtherLangRowIndex(rowIdx);
    const pv = allPhaseVariants[rowIdx];
    const t = pv?.tr?.[selectedLang] || {};

    // Split the translated symptoms and cures by semicolon for table display
    const symptomsArray = t.symptoms ? String(t.symptoms).split(';').filter(Boolean).map(s => s.trim()) : [];
    const curesArray = t.cure ? String(t.cure).split(';').filter(Boolean).map(c => c.trim()) : [];

    setOtherLangSymptomsList(symptomsArray);
    setOtherLangCuresList(curesArray);

    // Set the textarea inputs to the joined string
    setOtherLangSymptomsInput(t.symptoms || '');
    setOtherLangCureInput(t.cure || '');
  };

  const handleOtherLangUpdateRow = () => {
    if (editingOtherLangRowIndex == null) return;
    setAllPhaseVariants(prev => {
      const copy = [...prev];
      const row = { ...copy[editingOtherLangRowIndex] };
      row.tr = { ...(row.tr || {}) };

      // Use the lists if they have items, otherwise use the input text
      const finalSymptoms = otherLangSymptomsList.length > 0
        ? otherLangSymptomsList.join('$')
        : otherLangSymptomsInput;

      const finalCure = otherLangCuresList.length > 0
        ? otherLangCuresList.join('$')
        : otherLangCureInput;

      row.tr[selectedLang] = {
        symptoms: finalSymptoms,
        cure: finalCure,
      };
      // also persist to map (so save has them)
      row.SymptomsOtherLang = { ...(row.SymptomsOtherLang || {}) };
      row.CureOtherLang = { ...(row.CureOtherLang || {}) };
      row.SymptomsOtherLang[selectedLang] = finalSymptoms;
      row.CureOtherLang[selectedLang] = finalCure;

      copy[editingOtherLangRowIndex] = row;
      return copy;
    });
    setEditingOtherLangRowIndex(null);
    setOtherLangSymptomsInput('');
    setOtherLangCureInput('');
    setOtherLangSymptomsList([]);
    setOtherLangCuresList([]);
  };

  return (
    <>
      <style>{customStyles}</style>
      <Drawer
        open={isOpen}
        // onClose={onClose}
        direction="right"
        size={900}
        style={{ zIndex: 1050 }}
      >
        <div className="relative">
          <button
            variant="outline"
            onClick={onClose}
            style={{ border: 'none', color: '#000', padding: 0, textAlign: 'end', padding: '15px', position: 'absolute', top: 0, right: 0 }}
          >
            <Icon icon="lucide:x" className="text-2xl" />
          </button>
          <div className="card-body">
            <h6 className="text-xxl text-primary-light mb-16">
              {normalizedEditData?.cropDiseasesId
                ? 'Edit Disease Mapping'
                : 'Add New Disease Mapping'}
            </h6>

            {message && (
              <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
                {message.text}
              </div>
            )}

            {(loading || fetchLoading) && (
              <div className="text-center mb-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2">{fetchLoading ? 'Loading data...' : 'Saving...'}</span>
              </div>
            )}

            {/* Main Tabs */}
            <div className="main-tabs">
              <button
                type="button"
                className={`main-tab ${mainTab === 'english' ? 'active' : ''}`}
                onClick={() => setMainTab('english')}
              >
                English
              </button>
              <button
                type="button"
                className={`main-tab ${mainTab === 'other' ? 'active' : ''}`}
                onClick={() => setMainTab('other')}
                disabled={!diseaseMappingToEdit && step !== 'translations'}
                title={!diseaseMappingToEdit && step !== 'translations' ? 'Translate first to enable' : 'Other languages'}
              >
                Other Languages
              </button>
            </div>

            {/* Translating loader */}
            {step === 'translate' && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <h4>Translating…</h4>
                <p>Please wait...</p>
              </div>
            )}

            {/* ENGLISH TAB */}
            {mainTab === 'english' && step !== 'translate' && (
              <form onSubmit={(e) => e.preventDefault()}>
                {/* Crop + Disease */}
                <div className="form-grid-container">
                  <div>
                    <label htmlFor="crop" className="form-label">
                      Crop <span className="text-danger-600">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.crop ? 'border-danger-600' : ''} ${cropDisabled ? 'disabled' : ''}`}
                      id="crop"
                      ref={cropInputRef}
                      disabled={cropDisabled || loading || fetchLoading}
                      {...register('crop', { required: !cropDisabled && 'Crop is required' })}
                      onChange={handleCropChange}
                      value={selectedCrop}
                    >
                      <option value="">Select Crop</option>
                      {[...crops]
                        .sort((a, b) => a.CropName.localeCompare(b.CropName))
                        .map((crop) => (
                          <option key={crop.ID} value={crop.CropName} data-id={crop.ID}>
                            {crop.CropName}
                          </option>
                        ))}
                    </select>

                    {errors.crop && <span className="error-message">{errors.crop.message}</span>}
                  </div>

                  <div>
                    <label htmlFor="disease" className="form-label">
                      Disease <span className="text-danger-600">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.disease ? 'border-danger-600' : ''}`}
                      id="disease"
                      disabled={!selectedCrop || loading || fetchLoading}
                      {...register('disease', { required: 'Disease is required' })}
                      onChange={handleDiseaseChange}
                      value={selectedDisease}
                    >
                      <option value="">Select Disease</option>

                      {[...filteredDiseases]
                        .sort((a, b) =>
                          (a.DiseasesName || '').localeCompare(b.DiseasesName || '')
                        )
                        .map((disease) => (
                          <option key={disease.ID} value={disease.DiseasesName} data-id={disease.ID}>
                            {disease.DiseasesName}
                          </option>
                        ))}
                    </select>


                    {errors.disease && <span className="error-message">{errors.disease.message}</span>}
                  </div>
                </div>

                {/* Phase */}
                <div className="mb-20">
                  <label htmlFor="phase" className="form-label">
                    Phase <span className="text-danger-600">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.phase ? 'border-danger-600' : ''}`}
                    id="phase"
                    disabled={!selectedDisease || loading}
                    {...register('phase', { required: 'Phase is required' })}
                    onChange={(e) => setSelectedPhase(e.target.value)}
                    value={selectedPhase}
                  >
                    <option value="">Select Phase</option>
                    {phases.map((phase) => (
                      <option key={phase.ID} value={phase.PhaseName}>
                        {phase.PhaseName}
                      </option>
                    ))}
                  </select>
                  {errors.phase && <span className="error-message">{errors.phase.message}</span>}
                </div>

                {/* Symptoms + Cure */}
                <div className="form-grid-container mb-20">
                  <div>
                    <label htmlFor="symptoms" className="form-label">
                      Symptoms <span className="text-danger-600">*</span>
                    </label>
                    <textarea
                      id="symptoms"
                      placeholder="Enter Symptom"
                      className={`form-control ${errors.symptoms ? 'border-danger-600' : ''}`}
                      value={symptoms}
                      onChange={(e) => setSymptoms(e.target.value)}
                      disabled={loading}
                    />
                    {errors.symptoms && <span className="error-message">{errors.symptoms.message}</span>}
                    <button
                      type="button"
                      className="btn btn-green add-button"
                      onClick={handleAddSymptom}
                      disabled={loading || !symptoms.trim()}
                    >
                      Add Symptom
                    </button>
                  </div>

                  <div>
                    <label htmlFor="cure" className="form-label">
                      Cure <span className="text-danger-600">*</span>
                    </label>
                    <textarea
                      id="cure"
                      placeholder="Enter Cure"
                      className={`form-control ${errors.cure ? 'border-danger-600' : ''}`}
                      value={cure}
                      onChange={(e) => setCure(e.target.value)}
                      disabled={loading}
                    />
                    {errors.cure && <span className="error-message">{errors.cure.message}</span>}
                    <button
                      type="button"
                      className="btn btn-green add-button"
                      onClick={handleAddCure}
                      disabled={loading || !cure.trim()}
                    >
                      Add Cure
                    </button>
                  </div>
                </div>

                {/* Symptoms & Cures Tables */}
                {(symptomsList.length > 0 || curesList.length > 0) && (
                  <div className="tables-row mb-20">
                    <div>
                      <table className="table">
                        <thead>
                          <tr><th>Symptoms</th><th>Action</th></tr>
                        </thead>
                        <tbody>
                          {symptomsList.map((symptom, index) => (
                            <tr key={index}>
                              <td>{symptom}</td>
                              <td>
                                <button className="remove-btn" onClick={() => handleRemoveSymptom(index)} disabled={loading}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div>
                      <table className="table">
                        <thead>
                          <tr><th>Cures</th><th>Action</th></tr>
                        </thead>
                        <tbody>
                          {curesList.map((cureItem, index) => (
                            <tr key={index}>
                              <td>{cureItem}</td>
                              <td>
                                <button className="remove-btn" onClick={() => handleRemoveCure(index)} disabled={loading}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Add Phase Variant */}
                <div className="mb-20">
                  <button
                    type="button"
                    className="btn btn-green"
                    onClick={handleAddPhaseVariant}
                    disabled={loading || !selectedPhase || !selectedDiseaseId || symptomsList.length === 0 || curesList.length === 0}
                  >
                    Add Phase Details
                  </button>
                </div>

                {/* All Phase Variants Table (English) - Shows all diseases */}
                {allPhaseVariants.length > 0 && (
                  <div className="phase-variants-table">
                    <h6 className="text-md text-center signup-yellow mb-12">All Phase Details</h6>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Phase</th>
                          <th>Disease</th>
                          <th>Symptoms</th>
                          <th>Cure</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allPhaseVariants.map((pv, index) => (
                          <tr key={index}>
                            <td>{pv.phase}</td>
                            <td>{pv.diseaseName}</td>

                            <td>
                              <ul className="bullet-list">
                                {(pv.symptoms ? String(pv.symptoms).split('$') : [])
                                  .filter(Boolean)
                                  .map((s, i) => (
                                    <li key={i}>{s.trim()}</li>
                                  ))}
                                {!pv.symptoms && <li>-</li>}
                              </ul>
                            </td>

                            <td>
                              <ul className="bullet-list">
                                {(pv.cure ? String(pv.cure).split('$') : [])
                                  .filter(Boolean)
                                  .map((c, i) => (
                                    <li key={i}>{c.trim()}</li>
                                  ))}
                                {!pv.cure && <li>-</li>}
                              </ul>
                            </td>

                            <td style={{ display: 'flex', gap: 8 }}>
                              <button
                                className="remove-btn"
                                onClick={() => handleEditPhaseVariant(index)}
                                disabled={loading}
                              >
                                Edit
                              </button>

                              <button
                                className="remove-btn"
                                onClick={() => handleRemovePhaseVariant(index)}
                                disabled={loading}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Actions (English) — Only Translate */}
                <div className="d-flex align-items-center justify-content-center gap-3" style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-yellow border border-600 text-md px-56 py-12 radius-8"
                    onClick={onTranslateClick}
                    disabled={loading || allPhaseVariants.length === 0 || !selectedCropId}
                    title={!selectedCropId ? 'Select crop' : (allPhaseVariants.length === 0 ? 'Add at least one phase row' : 'Translate')}
                  >
                    Translate
                  </button>
                </div>
              </form>
            )}

            {/* OTHER LANGUAGES TAB */}
            {mainTab === 'other' && (
              <>
                {/* Language Tabs */}
                <div className="lang-tabs">
                  {languages.length === 0 ? (
                    <span style={{ opacity: 0.7 }}>No languages loaded</span>
                  ) : (
                    languages.map((lang) => (
                      <button
                        key={lang.GoogleLanguageCode}
                        type="button"
                        className={`lang-tab ${selectedLang === lang.GoogleLanguageCode ? 'active' : ''}`}
                        onClick={() => setSelectedLang(lang.GoogleLanguageCode)}
                      >
                        {lang.LanguageName}
                      </button>
                    ))
                  )}
                </div>

                {/* Translated chips: Crop + (all unique Diseases) */}
                <div style={{ marginBottom: 12 }}>
                  {selectedCrop && (
                    <span className="chip">
                      <strong>Crop:</strong>{' '}
                      {getOtherLangName(currentCropObj, 'CropName') || selectedCrop}
                    </span>
                  )}

                  {/* NEW: Show disease chips (unique by diseaseId) in selected language */}
                  {[...new Set(allPhaseVariants.map(p => p.diseaseId))].map((did) => {
                    const olName = getDiseaseOtherLangById(did, (allPhaseVariants.find(p => p.diseaseId === did)?.diseaseName) || '');
                    return (
                      <span key={did} className="chip">
                        <strong>Disease:</strong> {olName}
                      </span>
                    );
                  })}
                </div>

                {/* Per-language editor inputs */}
                <div className="form-grid-container" style={{ marginTop: 8 }}>
                  <div>
                    <label className="form-label">Symptoms ({selectedLangName})</label>
                    <textarea
                      className="form-control"
                      value={otherLangSymptomsInput}
                      onChange={(e) => setOtherLangSymptomsInput(e.target.value)}
                      placeholder={editingOtherLangRowIndex == null ? 'Click Edit on a row to start editing' : 'Enter translated symptoms'}
                    />
                    <button
                      type="button"
                      className="btn btn-green add-button"
                      onClick={handleAddOtherLangSymptom}
                      disabled={loading || !otherLangSymptomsInput.trim()}
                    >
                      Add Symptom
                    </button>
                  </div>
                  <div>
                    <label className="form-label">Cure ({selectedLangName})</label>
                    <textarea
                      className="form-control"
                      value={otherLangCureInput}
                      onChange={(e) => setOtherLangCureInput(e.target.value)}
                      placeholder={editingOtherLangRowIndex == null ? 'Click Edit on a row to start editing' : 'Enter translated cure'}
                    />
                    <button
                      type="button"
                      className="btn btn-green add-button"
                      onClick={handleAddOtherLangCure}
                      disabled={loading || !otherLangCureInput.trim()}
                    >
                      Add Cure
                    </button>
                  </div>
                </div>

                {/* Other Language Symptoms & Cures Tables - EXACTLY LIKE ENGLISH TAB */}
                {(otherLangSymptomsList.length > 0 || otherLangCuresList.length > 0) && (
                  <div className="tables-row mb-20">
                    <div>
                      <table className="table">
                        <thead>
                          <tr><th>Symptoms ({selectedLangName})</th><th>Action</th></tr>
                        </thead>
                        <tbody>
                          {otherLangSymptomsList.map((symptom, index) => (
                            <tr key={index}>
                              <td>{symptom}</td>
                              <td>
                                <button className="remove-btn" onClick={() => handleRemoveOtherLangSymptom(index)} disabled={loading}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div>
                      <table className="table">
                        <thead>
                          <tr><th>Cures ({selectedLangName})</th><th>Action</th></tr>
                        </thead>
                        <tbody>
                          {otherLangCuresList.map((cureItem, index) => (
                            <tr key={index}>
                              <td>{cureItem}</td>
                              <td>
                                <button className="remove-btn" onClick={() => handleRemoveOtherLangCure(index)} disabled={loading}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-center gap-3" style={{ margin: '8px 0 16px' }}>
                  <button
                    type="button"
                    className="btn btn-yellow border border-600 text-md px-56 py-12 radius-8"
                    onClick={handleOtherLangUpdateRow}
                    disabled={editingOtherLangRowIndex == null || (!otherLangSymptomsInput.trim() && otherLangSymptomsList.length === 0) || (!otherLangCureInput.trim() && otherLangCuresList.length === 0)}
                    title={editingOtherLangRowIndex == null ? 'Choose a row to edit' : 'Update row translation'}
                  >
                    {editingOtherLangRowIndex == null ? 'Select a Row' : 'Update Row'}
                  </button>
                </div>

                {/* All Phase Details Table in selected language */}
                {allPhaseVariants.length > 0 ? (
                  <div className="phase-variants-table">
                    <h6 className="text-md text-center signup-yellow mb-12">All Phase Details ({selectedLangName})</h6>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Phase</th>
                          <th>Disease</th>
                          <th>Symptoms</th>
                          <th>Cure</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allPhaseVariants.map((pv, index) => {
                          const tr = pv.tr?.[selectedLang] || { symptoms: '', cure: '' };
                          const phaseNameOL = getOtherLangPhaseName(pv.phase);
                          const diseaseOL = getDiseaseOtherLangById(pv.diseaseId, pv.diseaseName); // <-- localized disease
                          return (
                            <tr key={index}>
                              <td>{phaseNameOL}</td>
                              <td>{diseaseOL}</td>
                              <td>
                                <ul className="bullet-list">
                                  {(tr.symptoms ? String(tr.symptoms).split('$') : []).filter(Boolean).map((s, i) => (
                                    <li key={i}>{s.trim()}</li>
                                  ))}
                                  {!tr.symptoms && <li>-</li>}
                                </ul>
                              </td>
                              <td>
                                <ul className="bullet-list">
                                  {(tr.cure ? String(tr.cure).split('$') : []).filter(Boolean).map((c, i) => (
                                    <li key={i}>{c.trim()}</li>
                                  ))}
                                  {!tr.cure && <li>-</li>}
                                </ul>
                              </td>
                              <td style={{ display: 'flex', gap: 8 }}>
                                <button
                                  className="remove-btn"
                                  onClick={() => handleOtherLangEditRow(index)}
                                  disabled={loading}
                                >
                                  Edit
                                </button>
                                <button
                                  className="remove-btn"
                                  onClick={() => handleRemovePhaseVariant(index)}
                                  disabled={loading}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ opacity: 0.7, marginBottom: 12 }}>No phase rows to translate.</div>
                )}

                {/* Actions (Other Languages) — Save only here */}
                <div className="d-flex align-items-center justify-content-center gap-3" style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-yellow border border-600 text-md px-56 py-12 radius-8"
                    onClick={handleSubmit(onFormSubmit)}
                    disabled={loading || allPhaseVariants.length === 0 || isAnyOtherLangEmpty}
                    title={isAnyOtherLangEmpty ? 'Fill Symptoms & Cure for every language in each row' : 'Save'}
                  >
                    {loading
                      ? 'Saving...'
                      : normalizedEditData?.cropDiseasesId
                        ? 'Update'
                        : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default AddDiseaseMapping;